/**
 * @file The consultation transcript feed — one id-keyed store, chronological order, and the
 *       transcriber's health as the practitioner reads it.
 * @module hooks/useConsultTranscripts
 *
 * S24 of ClickUp 86d3zmpwv. Consumed by both practitioner consult pages
 * (`InPersonConsultation`, `VideoConsultation`), which each held their own copy of the logic
 * below and had already drifted apart.
 *
 * ═══ WHY DEDUPE IS BY SERVER ID AND NOTHING ELSE ══════════════════════════════════════════
 * Both pages used to merge a `transcriptUpdate` into the feed by: same `id`, OR same
 * `serverProcessedId` (a field **no producer has ever written** — it was pure dead weight), OR
 * same `participantId` within 2000ms AND one string containing the other — and then kept the
 * LONGER of the two strings.
 *
 * That heuristic silently destroys clinical content. Two of the failure modes are routine:
 *   • A revision that CORRECTS a segment is usually shorter than the guess it replaces
 *     ("the patient takes ten milligrams" → "the patient takes 10mg"), and "keep the longer
 *     string" throws the correction away and keeps the wrong one.
 *   • Two genuine utterances from the same speaker seconds apart, where one is a substring of
 *     the other ("yes" inside "yes, since Tuesday"), collapse into one segment.
 *
 * transcribe has published a stable per-segment `id` since the pipeline upgrade, and talk
 * synthesises one for anything that arrives without (`transcriptRouter.js::normaliseTranscript`),
 * so the id is always present and always authoritative. A re-arrival with the same id REPLACES
 * the entry outright — that is how a revision lands, and a shorter revision wins because it is
 * newer, not because it is longer.
 *
 * ═══ WHY THE ORDER KEY IS `absoluteStart` ════════════════════════════════════════════════
 * `timestamp` is when the segment was PUBLISHED, so it orders by when inference happened to
 * finish — which reorders speech whenever one utterance takes longer to transcribe than the one
 * after it. `absoluteStart` is when the words were SAID. It is optional (an older transcribe
 * does not send it), so `timestamp` remains the fallback rather than the primary key.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Transcript } from "../../types/appointments/ConsultTypes";

// ─── Segment normalisation ────────────────────────────────────────────────────────────────

/**
 * Coerce a wire/stored timestamp to epoch ms.
 *
 * Tolerant of a numeric string and of an ISO date because the stored column has carried both
 * over the years — the pre-S24 code reached for `new Date(t.timestamp)` for the same reason.
 */
const toMillis = (value: unknown, fallback: number): number => {
	if (typeof value === "number" && Number.isFinite(value)) return value;

	if (typeof value === "string" && value.trim()) {
		const asNumber = Number(value);
		if (Number.isFinite(asNumber)) return asNumber;

		const asDate = new Date(value).getTime();
		if (Number.isFinite(asDate)) return asDate;
	}

	return fallback;
};

/**
 * One socket payload or stored row → a `Transcript`, or `null` if it is not one.
 *
 * The id falls back to `{participantId}-{timestamp}` only for a producer old enough not to send
 * one. It is a weaker key than the server's — two segments from one microphone in the same
 * millisecond would collide — but it is the same key the feed used before, and every live
 * producer now supplies a real id.
 */
export const toTranscript = (payload: unknown): Transcript | null => {
	if (!payload || typeof payload !== "object") return null;

	const raw = payload as Record<string, unknown>;
	if (typeof raw.transcript !== "string") return null;

	const participantId = typeof raw.participantId === "string" ? raw.participantId : "unknown";
	const timestamp = toMillis(raw.timestamp, Date.now());
	const id = typeof raw.id === "string" && raw.id ? raw.id : `${participantId}-${timestamp}`;
	// start/end only when finite, so a seek player never inherits a bad wire value.
	const start = typeof raw.start === "number" && Number.isFinite(raw.start) ? raw.start : undefined;
	const end = typeof raw.end === "number" && Number.isFinite(raw.end) ? raw.end : undefined;

	return { ...(raw as unknown as Transcript), id, participantId, timestamp, start, end };
};

/**
 * Any stored/broadcast transcript value → a usable array. An array, a JSON string, a
 * double-encoded JSON string, `null`, `""` or outright garbage all resolve; nothing throws.
 *
 * The completed-consult view and the `roomInfo` snapshot both used to call `JSON.parse` bare, so
 * one malformed column blanked the whole page instead of the transcript panel. The second unwrap
 * is not speculative: healthcare stores the column as a JSON string and has been double-encoding
 * it on at least one write path, so a later fix at that end must not need a frontend release.
 */
export const safeParseTranscripts = (value: unknown): Transcript[] => {
	let candidate: unknown = value;

	// At most two unwraps: a JSON string, and a JSON string that decoded to another JSON string.
	for (let depth = 0; depth < 2 && typeof candidate === "string"; depth += 1) {
		const trimmed = candidate.trim();
		if (!trimmed) return [];

		try {
			candidate = JSON.parse(trimmed);
		} catch {
			return [];
		}
	}

	if (!Array.isArray(candidate)) return [];

	return candidate.map(toTranscript).filter((segment): segment is Transcript => segment !== null);
};

// The row's display clock and its sort key are the same field, on purpose — see the module comment.
export const transcriptSaidAt = (segment: Transcript): number => (typeof segment.absoluteStart === "number" ? segment.absoluteStart : segment.timestamp);

/**
 * Fold a bulk-loaded array into `target`, resolving a duplicate id by publication `timestamp` —
 * the LATER arrival wins.
 *
 * A plain `forEach(target.set(...))` is last-write-wins, and `roomInfo` carries the room newest
 * first (`talk/server.js::roomTranscriptsField` reverses the chronological list at the edge), so
 * last-write-wins keeps the entry a revision REPLACED. talk's `appendTranscript` stores a revision
 * as a second entry under the same id and delegates the choice here in as many words — "the client
 * keeps the later arrival" — so this is the contract, not a preference.
 *
 * A tie keeps whichever entry was already in `target`, which leaves the array's own order deciding
 * when the timestamps cannot, and leaves a live segment in place against an identical snapshot copy.
 */
const foldSegments = (target: Map<string, Transcript>, segments: Transcript[]): Map<string, Transcript> => {
	segments.forEach((segment) => {
		const existing = target.get(segment.id);
		if (!existing || segment.timestamp > existing.timestamp) target.set(segment.id, segment);
	});

	return target;
};

/** A stored `completed_appointments.transcript` value → ordered, deduped rows — the pure half of `setAll`. */
export const parseStoredTranscripts = (value: unknown): Transcript[] =>
	Array.from(foldSegments(new Map<string, Transcript>(), safeParseTranscripts(value)).values()).sort((a, b) => transcriptSaidAt(a) - transcriptSaidAt(b));

export interface ConsultTranscriptFeed {
	/** Every known segment, oldest first. */
	transcripts: Transcript[];
	/** A live `transcriptUpdate` payload. Same id ⇒ replaces; unknown id ⇒ inserted in order. */
	upsert: (payload: unknown) => void;
	/** A bulk load UNIONED into the feed — the `roomInfo` snapshot. Never removes a segment. */
	upsertMany: (value: unknown) => void;
	/** A bulk load that REPLACES the feed — a completed consult's stored column. */
	setAll: (value: unknown) => void;
}

/**
 * The transcript feed for one consultation.
 *
 * State is a `Map` keyed by segment id, so an id is stored once no matter how many times it
 * arrives, and the rendered array is derived from it. `Array.prototype.sort` is stable and a
 * `Map` iterates in insertion order, so segments sharing an order key stay in arrival order.
 */
export const useConsultTranscripts = (): ConsultTranscriptFeed => {
	const [byId, setById] = useState<Map<string, Transcript>>(() => new Map());

	const upsert = useCallback((payload: unknown) => {
		const segment = toTranscript(payload);
		if (!segment) return;

		setById((prev) => {
			const next = new Map(prev);
			// Replace, never merge: a revision is the whole truth about that segment, and merging
			// would let a stale `absoluteStart` or a longer earlier guess survive underneath it.
			next.set(segment.id, segment);
			return next;
		});
	}, []);

	/**
	 * The `roomInfo` snapshot path — a UNION, never a replacement.
	 *
	 * `roomInfo` is not a one-off: the pages emit `joinRoom` on every connect and talk answers
	 * every `joinRoom` with a full snapshot, so this runs again after each reconnect. It cannot
	 * discard the local feed, because talk emits `transcriptUpdate` BEFORE it appends to Redis
	 * (`transcriptRouter.js` — the UI must not wait on a Redis write). A segment caught in that
	 * window is shown live and is absent from the very next snapshot; replacing the feed would
	 * erase it from the screen AND from the saved record, silently and permanently.
	 *
	 * An empty parse is a no-op rather than a clear, for the same reason.
	 */
	const upsertMany = useCallback((value: unknown) => {
		const segments = safeParseTranscripts(value);
		if (segments.length === 0) return;

		setById((prev) => foldSegments(new Map(prev), segments));
	}, []);

	// Shares parseStoredTranscripts's fold + sort, so the hook and the pure helper cannot drift apart.
	const setAll = useCallback((value: unknown) => setById(new Map(parseStoredTranscripts(value).map((segment) => [segment.id, segment]))), []);

	const transcripts = useMemo(() => Array.from(byId.values()).sort((a, b) => transcriptSaidAt(a) - transcriptSaidAt(b)), [byId]);

	return { transcripts, upsert, upsertMany, setAll };
};

// ─── Transcriber health ───────────────────────────────────────────────────────────────────

export type TranscriberState = "ok" | "degraded" | "error";

export interface TranscriberStatus {
	state: TranscriberState;
	/** Ready to render. Empty only for `ok`, which is rendered as "no banner". */
	message: string;
}

/**
 * Reason/error code → the sentence the practitioner reads.
 *
 * ⚠️ An ALLOW-LIST, and the fallbacks below are deliberately generic. transcribe publishes
 * `str(e)` as the error code on its catch-all path (`redis_processor.py:764`), so rendering an
 * unrecognised value verbatim would put a raw exception message — which can carry a file path, a
 * payload fragment or clinical text — on a clinician's screen. Same rule as Xoe's tool labels:
 * a vocabulary the server owns is never echoed back unrecognised.
 */
const TRANSCRIBER_STATUS_MESSAGES: Record<string, string> = {
	// talk — services/audioRouter.js
	queue_overflow: "Audio is arriving faster than it can be transcribed. Some speech may be missing.",
	segment_lost: "Part of the audio did not arrive. Some speech may be missing.",
	// transcribe — app/redis_processor.py
	transcription_timeout: "Transcription is taking longer than expected. Some speech may be missing.",
};

const GENERIC_DEGRADED = "Transcription is degraded. Some speech may be missing.";
const GENERIC_ERROR = "Some audio could not be transcribed.";

/**
 * `hasOwnProperty`, never a bare `TRANSCRIBER_STATUS_MESSAGES[code]`: the code arrives off the
 * socket, and a bare read on an object literal returns an INHERITED member for `toString` or
 * `constructor` — i.e. a function where a string is typed.
 */
const messageFor = (code: string, fallback: string): string =>
	(code && Object.prototype.hasOwnProperty.call(TRANSCRIBER_STATUS_MESSAGES, code) ? TRANSCRIBER_STATUS_MESSAGES[code] : "") || fallback;

const asRecord = (payload: unknown): Record<string, unknown> => (payload && typeof payload === "object" ? (payload as Record<string, unknown>) : {});

/** A `transcriberStatus` frame from talk (`services/audioRouter.js`): `{roomId, state, reason}`. */
export const transcriberStatusFromEvent = (payload: unknown): TranscriberStatus => {
	const raw = asRecord(payload);
	const state: TranscriberState = raw.state === "ok" || raw.state === "error" ? raw.state : "degraded";

	if (state === "ok") return { state, message: "" };

	return { state, message: messageFor(typeof raw.reason === "string" ? raw.reason : "", state === "error" ? GENERIC_ERROR : GENERIC_DEGRADED) };
};

/** A `transcriptionError` frame from talk (`services/transcriptRouter.js`): `{roomId, error, …}`. */
export const transcriberStatusFromError = (payload: unknown): TranscriberStatus => {
	const raw = asRecord(payload);

	return { state: "error", message: messageFor(typeof raw.error === "string" ? raw.error : "", GENERIC_ERROR) };
};

/** How long a degradation stays on screen once nothing further has been reported. */
export const TRANSCRIBER_STATUS_CLEAR_MS = 15000;

export interface TranscriberStatusFeed {
	/** The banner to render, or `null` for "nothing to say". */
	status: TranscriberStatus | null;
	/** Stable across renders, so a `[]`-dep socket effect can register it once. */
	report: (next: TranscriberStatus) => void;
}

/**
 * The transcriber-health banner for one consultation.
 *
 * Auto-clears after a quiet window because recovery is SILENT — nothing on the wire says "ok"
 * again — so a sticky banner would outlive the problem for the rest of the consult. Every fresh
 * report restarts the window, so a fault that keeps recurring keeps the banner up.
 */
export const useTranscriberStatus = (): TranscriberStatusFeed => {
	const [status, setStatus] = useState<TranscriberStatus | null>(null);
	const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const report = useCallback((next: TranscriberStatus) => {
		if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
		clearTimerRef.current = null;

		if (next.state === "ok") {
			setStatus(null);
			return;
		}

		setStatus(next);
		clearTimerRef.current = setTimeout(() => setStatus(null), TRANSCRIBER_STATUS_CLEAR_MS);
	}, []);

	useEffect(
		() => () => {
			if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
		},
		[],
	);

	return { status, report };
};

// ─── Local capture state ──────────────────────────────────────────────────────────────────

export type CaptureState = "recording" | "off" | "stopped";

/** Authored here, never interpolated from a wire value — same allow-list rule as the messages above. */
export const CAPTURE_STATE_LABELS: Record<CaptureState, string> = {
	recording: "Recording",
	off: "Transcription off",
	stopped: "Not recording",
};

export const CAPTURE_STOPPED_MESSAGE = "This consultation is not being recorded — speech from this device is not reaching the transcript.";
export const CAPTURE_STOPPED_ACTION = "Resume recording";

/**
 * Which capture state the practitioner is in, or `null` when the consult has not started.
 *
 * ⚠️ `running` is the PRESENCE OF THE CAPTURE SERVICE, never `transcriberActive` — that flag tracks
 * detected voice, so keying the warning off it would raise it every time nobody is speaking.
 */
export const captureStateFor = (started: boolean, enabled: boolean, running: boolean): CaptureState | null => {
	if (!started) return null;
	if (running) return "recording";

	return enabled ? "stopped" : "off";
};
